import { Alert, Box, styled } from "@mui/material";
import { IAircraftGroup, IFlightType, IPosition } from "@vmosolutions/oc-interfaces";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { saveFlightPairingStatusUserSettings } from "src/API/FlightPairingStatusV2";
import { DialogModalWrapper, EventModalTypes } from "src/components/DialogModal/DialogModalWrapper";
import { updateUserAppSettings } from "src/redux/actions/App.Actions";
import { openContextMenu } from "src/redux/actions/ContextMenu.Actions";
import {
  flightPairingStatusWindowClosed,
  flightPairingStatusWindowOpened,
  loadFlightPairingStatus,
  recomputeFlightPairingStatus,
  reloadFlightPairingStatus,
  selectFlightPairingStatus,
} from "src/redux/actions/FlightPairingStatusV2/FlightPairingStatusV2.Actions";
import { openSnackbar } from "src/redux/actions/App.Actions";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import FlightPairingStatusV2ContextMenu from "./FlightPairingStatusV2ContextMenu";
import { PairingStatusCalendarGrid, PairingStatusGridContext } from "./PairingStatusCalendarGrid";
import { PairingStatusFilters } from "./PairingStatusFilters";
import { PairingStatusHeader, PairingStatusView } from "./PairingStatusHeader";
import { PairingStatusListGrid } from "./PairingStatusListGrid";
import {
  IPairingStatusFilters,
  buildDayColumns,
  compareRowsByDeparture,
  countByStatus,
  currentMonthRange,
  distinctAirports,
  filterRows,
  groupRowsByRoute,
  hasActiveFilters,
  oldestScheduledEvaluation,
} from "./pairingStatusSelectors";
import { IEpochRange, IFlightPairingStatusRow } from "./pairingStatusTypes";
import {
  IFlightPairingStatusUserSettings,
  fromFilterSelection,
  isViewSetting,
  sameSelection,
  toFilterSelection,
} from "./pairingStatusUserSettings";

interface IFlightPairingStatusV2WindowProps {
  id: string;
  path: string[];
  windowProps: any;
}

const Root = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  height: "100%",
  minHeight: 0,
  minWidth: 0,
  backgroundColor: theme.custom.timeline.contextListBackground,
  position: "relative",
  "& .grid_area": { flex: "1 1 auto", minHeight: 0, minWidth: 0, position: "relative" },
}));

/** A tab hidden for longer than this is reloaded when it comes back (websocket frames may have been missed). */
const RELOAD_AFTER_HIDDEN_MS = 60_000;

/**
 * Measure an element's height with a ResizeObserver so the grid can be given an explicit pixel height even if
 * the flex chain above it does not resolve to a definite height (see MosaicTabPanel).
 */
const useMeasuredHeight = (ref: React.RefObject<HTMLElement>): number => {
  const [height, setHeight] = useState<number>(0);
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const update = (): void => setHeight(Math.floor(node.getBoundingClientRect().height));
    update();
    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(update);
      observer.observe(node);
    }
    window.addEventListener("resize", update);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [ref]);
  return height;
};

/**
 * Flight Pairing Status: which flights are (not) covered by crewed pairings.
 *
 * The data is a server-side cache that the backend keeps up to date (crew / pairing / flight change events plus
 * a scheduled safety net) and pushes changes for over the websocket, so the window loads as soon as it opens,
 * every filter is applied instantly on the client, and "Recompute" is only needed when something looks off.
 */
export const FlightPairingStatusV2Window = (props: IFlightPairingStatusV2WindowProps): JSX.Element => {
  const { id, path } = props;
  const dispatch = useDispatch();

  // The last used filters and view live on the user's profile; the date range always starts at this month.
  const savedSettings: IFlightPairingStatusUserSettings | undefined = useSelector(
    (state: IRootState) => state.authentication.appSettings?.flightPairingStatus
  );
  const positionList = useSelector((state: IRootState) => state.structures.customerStructure.positions);
  const [restored] = useState(() =>
    fromFilterSelection(savedSettings?.lastFilters, { aircraftGroups: null, positions: positionList })
  );
  const pendingGroupIds = useRef<string[]>(restored.pendingAircraftGroupIds);
  // Whether saved aircraft groups still wait for the group list; state (not the ref) so the dirty check re-runs once
  // they are resolved, even when none of them exists any more and the filters do not change.
  const [groupsPending, setGroupsPending] = useState<boolean>(restored.pendingAircraftGroupIds.length > 0);

  const [range, setRange] = useState<IEpochRange>(() => currentMonthRange());
  const [view, setView] = useState<PairingStatusView>(() =>
    isViewSetting(savedSettings?.view) ? savedSettings.view : "calendar"
  );
  const [filters, setFilters] = useState<IPairingStatusFilters>(restored.filters);
  const [activeEventModal, setActiveEventModal] = useState<EventModalTypes>(EventModalTypes.None);

  const rowsById = useSelector((state: IRootState) => state.flightPairingStatusV2.rows);
  const isLoading = useSelector((state: IRootState) => state.flightPairingStatusV2.isLoading);
  const isRecomputing = useSelector((state: IRootState) => state.flightPairingStatusV2.isRecomputing);
  const loadError = useSelector((state: IRootState) => state.flightPairingStatusV2.loadError);
  const meta = useSelector((state: IRootState) => state.flightPairingStatusV2.meta);
  const loadedAt = useSelector((state: IRootState) => state.flightPairingStatusV2.loadedAt);
  const lastUpdateAt = useSelector((state: IRootState) => state.flightPairingStatusV2.lastUpdateAt);
  const selectedFlightId = useSelector((state: IRootState) => state.flightPairingStatusV2.selectedFlightId);
  // Only the id: the hovered flight object changes identity on every timeline mouse move.
  const hoveredTimelineFlightId = useSelector((state: IRootState) => state.timelineFlights.hoveredFlight?._id ?? null);

  // Select the raw structures and derive the option lists with useMemo: a selector that builds a new array on
  // every call would re-render this window on every store update.
  const flightTypeMap = useSelector((state: IRootState) => state.structures.customerStructure.flightTypes);
  const airportOptions = useSelector((state: IRootState) => state.structures.airportOptions);
  const flightTypes: IFlightType[] = useMemo(() => Object.values(flightTypeMap ?? {}), [flightTypeMap]);
  const positionOptions: IPosition[] = useMemo(
    () => [...(positionList ?? [])].sort((a, b) => (b.displayOrder > a.displayOrder ? -1 : 1)),
    [positionList]
  );
  const airportNames = useMemo(() => {
    const names: Record<string, string> = {};
    for (const option of Object.values(airportOptions ?? {}))
      if (option?.ident && option.name) names[option.ident] = option.name;
    return names;
  }, [airportOptions]);

  // Aircraft groups are only needed for the filter; load them once.
  const [aircraftGroupOptions, setAircraftGroupOptions] = useState<IAircraftGroup[]>([]);
  useEffect(() => {
    let cancelled = false;
    const getGroups = async (): Promise<void> => {
      const response = await AmplifyAPIWrapper.Get<IAircraftGroup[]>("/aircraftGroup");
      if (!cancelled && Array.isArray(response)) {
        const groups = [...response].sort((a, b) => a.title.toLowerCase().localeCompare(b.title.toLowerCase()));
        setAircraftGroupOptions(groups);
        // Restore the saved aircraft group filter now that the groups are known (ids that no longer exist drop out).
        if (pendingGroupIds.current.length > 0) {
          const ids = pendingGroupIds.current;
          pendingGroupIds.current = [];
          const restoredGroups = groups.filter((g) => ids.includes(g._id));
          if (restoredGroups.length > 0) {
            setFilters((current) =>
              current.aircraftGroups.length === 0 ? { ...current, aircraftGroups: restoredGroups } : current
            );
          }
        }
        setGroupsPending(false);
      }
    };
    void getGroups();
    return () => {
      cancelled = true;
    };
  }, []);

  // Saving is explicit: "Save filters" stores the current filters and view on the profile as the starting point
  // for the next time the window opens. Nothing is written until the user asks.
  const [isSavingFilters, setIsSavingFilters] = useState<boolean>(false);
  const filtersDirty = useMemo(() => {
    // Groups still being resolved: the comparison would be wrong until they are applied.
    if (groupsPending) return false;
    return (
      !sameSelection(toFilterSelection(filters), savedSettings?.lastFilters) ||
      view !== (savedSettings?.view ?? "calendar")
    );
  }, [filters, view, savedSettings, groupsPending]);
  const saveFilters = useCallback((): void => {
    setIsSavingFilters(true);
    const settings: IFlightPairingStatusUserSettings = {
      lastFilters: toFilterSelection(filters),
      view,
      savedAt: Date.now(),
    };
    void saveFlightPairingStatusUserSettings(settings)
      .then((stored) => {
        if (stored) {
          dispatch(updateUserAppSettings({ flightPairingStatus: stored }));
          dispatch(
            openSnackbar({
              open: true,
              closeDelay: 3000,
              variant: "success",
              message: "Filters saved - they will be applied the next time you open Flight Pairing Status v2",
            })
          );
        } else {
          dispatch(
            openSnackbar({ open: true, closeDelay: 4000, variant: "error", message: "Unable to save the filters" })
          );
        }
      })
      .finally(() => setIsSavingFilters(false));
  }, [dispatch, filters, view]);

  // Register with the store so websocket updates are only applied while a window is open.
  useEffect(() => {
    dispatch(flightPairingStatusWindowOpened());
    return () => {
      dispatch(flightPairingStatusWindowClosed());
    };
  }, [dispatch]);

  // Load (and reload on range change). Live updates are applied through the websocket handler.
  useEffect(() => {
    void dispatch(loadFlightPairingStatus(range));
  }, [dispatch, range]);

  // A tab that was hidden for a while may have missed websocket frames: reload what the server has.
  useEffect(() => {
    let hiddenAt: number | null = null;
    const onVisibility = (): void => {
      if (document.visibilityState === "hidden") {
        hiddenAt = Date.now();
      } else if (hiddenAt && Date.now() - hiddenAt > RELOAD_AFTER_HIDDEN_MS) {
        hiddenAt = null;
        void dispatch(reloadFlightPairingStatus());
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [dispatch]);

  const allRows = useMemo(() => Object.values(rowsById), [rowsById]);
  const staleCheckOldest = useMemo(() => oldestScheduledEvaluation(allRows, meta), [allRows, meta]);
  const filteredRows = useMemo(() => filterRows(allRows, filters), [allRows, filters]);
  // Status chip counts ignore the status filter itself so the chips show what each status would reveal.
  const statusCounts = useMemo(
    () => countByStatus(filterRows(allRows, { ...filters, statuses: [], problemsOnly: false })),
    [allRows, filters]
  );
  const routeRows = useMemo(() => groupRowsByRoute(filteredRows), [filteredRows]);
  const listRows = useMemo(() => [...filteredRows].sort(compareRowsByDeparture), [filteredRows]);
  const dayColumns = useMemo(() => buildDayColumns(range), [range]);
  const airports = useMemo(() => distinctAirports(allRows), [allRows]);

  const onSelect = useCallback(
    (row: IFlightPairingStatusRow): void => {
      dispatch(selectFlightPairingStatus(row.flightId));
      dispatch(openContextMenu(id));
    },
    [dispatch, id]
  );

  const gridInteraction = useMemo(
    () => ({ selectedFlightId, highlightedFlightId: hoveredTimelineFlightId, onSelect }),
    [selectedFlightId, hoveredTimelineFlightId, onSelect]
  );

  const onRecompute = useCallback((): void => {
    void dispatch(recomputeFlightPairingStatus(range)).then((result) => {
      if (!result) return;
      dispatch(
        openSnackbar({
          open: true,
          closeDelay: 4000,
          variant: "info",
          message: result.skipped
            ? "Already recomputed moments ago - showing that result"
            : `Recomputed ${result.computed} flights: ${result.changed} changed, ${result.removed} removed`,
        })
      );
    });
  }, [dispatch, range]);

  const onReload = useCallback((): void => {
    void dispatch(reloadFlightPairingStatus());
  }, [dispatch]);

  // The grid is given an explicit height: whatever the window has left after the header and filter bars.
  const rootRef = useRef<HTMLDivElement>(null);
  const topRef = useRef<HTMLDivElement>(null);
  const rootHeight = useMeasuredHeight(rootRef);
  const topHeight = useMeasuredHeight(topRef);
  const gridHeight = rootHeight > 0 ? Math.max(120, rootHeight - topHeight) : undefined;

  const emptyMessage = isLoading
    ? "Loading flight pairing status..."
    : allRows.length === 0
      ? "No flights in this range"
      : hasActiveFilters(filters)
        ? "No flights match the current filters"
        : "No flights to show";

  return (
    <>
      <Root ref={rootRef} id={`${id}-flightPairingStatusV2`}>
        <FlightPairingStatusV2ContextMenu id={id} path={path} setActiveEventModal={setActiveEventModal} />
        <Box ref={topRef}>
          <PairingStatusHeader
            range={range}
            onRangeChange={setRange}
            view={view}
            onViewChange={setView}
            meta={meta}
            staleCheckOldest={staleCheckOldest}
            loadedAt={loadedAt}
            lastUpdateAt={lastUpdateAt}
            isLoading={isLoading}
            isRecomputing={isRecomputing}
            onRecompute={onRecompute}
            onReload={onReload}
            flightCount={filteredRows.length}
            routeCount={routeRows.length}
          />
          <PairingStatusFilters
            filters={filters}
            onChange={setFilters}
            onSave={saveFilters}
            canSave={filtersDirty}
            isSaving={isSavingFilters}
            hasSavedFilters={!!savedSettings?.lastFilters}
            counts={statusCounts}
            origins={airports.origins}
            destinations={airports.destinations}
            airportNames={airportNames}
            flightTypes={flightTypes}
            aircraftGroups={aircraftGroupOptions}
            positions={positionOptions}
          />
          {loadError && (
            <Alert severity="error" sx={{ margin: "0px 8px 4px 8px", padding: "0px 8px" }}>
              {loadError}
            </Alert>
          )}
        </Box>
        <Box className="grid_area" style={gridHeight ? { height: gridHeight, flex: "0 0 auto" } : undefined}>
          <PairingStatusGridContext.Provider value={gridInteraction}>
            {view === "calendar" ? (
              <PairingStatusCalendarGrid
                routes={routeRows}
                dayColumns={dayColumns}
                loading={isLoading}
                emptyMessage={emptyMessage}
              />
            ) : (
              <PairingStatusListGrid rows={listRows} loading={isLoading} emptyMessage={emptyMessage} />
            )}
          </PairingStatusGridContext.Provider>
        </Box>
      </Root>
      {activeEventModal !== EventModalTypes.None && (
        <DialogModalWrapper modalType={activeEventModal} setActiveEventModal={setActiveEventModal} />
      )}
    </>
  );
};
